import { APP } from '../app/tokens';
import { PDF_VIEWER } from './pdfViewerTokens';
import AddFieldToolbar, { type PlacementType } from './AddFieldToolbar';
import type { SignerColor } from '../../lib/signerColors';

interface PdfViewerToolbarProps {
  currentPage: number;
  numPages: number;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitWidth: () => void;
  showNextField?: boolean;
  nextFieldLabel?: string;
  onNextField?: () => void;
  /** v1 placement-redesign: render AddFieldToolbar inline. Only set in
   *  configure mode by PdfSigningViewer; sign mode leaves these undefined
   *  and the buttons don't render. */
  placementMode?: PlacementType | null;
  onTogglePlacement?: (type: PlacementType) => void;
  activeSignerColor?: SignerColor;
  addFieldDisabled?: boolean;
  onApplySignature?: () => void;
  applySignatureDisabled?: boolean;
}

export default function PdfViewerToolbar({
  currentPage, numPages, zoom,
  onZoomIn, onZoomOut, onFitWidth,
  showNextField, nextFieldLabel, onNextField,
  placementMode, onTogglePlacement, activeSignerColor, addFieldDisabled,
  onApplySignature, applySignatureDisabled,
}: PdfViewerToolbarProps) {
  const btnStyle: React.CSSProperties = {
    background: 'transparent', border: `1px solid ${APP.border}`, borderRadius: 6,
    padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: APP.textPrimary,
    display: 'flex', alignItems: 'center', gap: 4,
  };

  return (
    <div style={{
      height: PDF_VIEWER.toolbarHeight, display: 'flex', alignItems: 'center',
      gap: 8, padding: '0 12px',
      borderBottom: `1px solid ${APP.border}`,
      backgroundColor: APP.bgContent,
      flexShrink: 0,
    }}>
      {/* Page indicator */}
      <span style={{ fontSize: 12, color: APP.textSecondary, minWidth: 80 }}>
        Page {currentPage} of {numPages}
      </span>

      {/* v1 placement-redesign: + Signature / + Date / + Name / + Initial.
          Only renders when the parent (PdfSigningViewer in configure mode)
          provides the toggle callback. */}
      {onTogglePlacement && (
        <AddFieldToolbar
          placementMode={placementMode ?? null}
          onTogglePlacement={onTogglePlacement}
          activeSignerColor={activeSignerColor}
          disabled={addFieldDisabled}
        />
      )}

      {onApplySignature && (
        <button
          type="button"
          onClick={onApplySignature}
          disabled={applySignatureDisabled}
          title={applySignatureDisabled ? 'Place at least one marker for yourself first' : 'Apply your signature, initials, date, name, and title markers'}
          style={{
            ...btnStyle,
            backgroundColor: applySignatureDisabled ? APP.bgContent : PDF_VIEWER.fieldSigned,
            color: applySignatureDisabled ? APP.textMuted : '#fff',
            border: applySignatureDisabled ? `1px solid ${APP.border}` : 'none',
            fontWeight: 700,
            opacity: applySignatureDisabled ? 0.6 : 1,
            cursor: applySignatureDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          Apply changes
        </button>
      )}

      <div style={{ flex: 1 }} />

      {/* Zoom controls */}
      <button style={btnStyle} onClick={onZoomOut} title="Zoom out">-</button>
      <span style={{ fontSize: 11, color: APP.textMuted, minWidth: 40, textAlign: 'center' }}>{zoom}%</span>
      <button style={btnStyle} onClick={onZoomIn} title="Zoom in">+</button>
      <button style={btnStyle} onClick={onFitWidth} title="Fit width">Fit</button>

      {/* Next field */}
      {showNextField && onNextField && (
        <>
          <div style={{ width: 1, height: 20, backgroundColor: APP.border }} />
          <button
            style={{
              ...btnStyle,
              backgroundColor: PDF_VIEWER.fieldUnsigned,
              color: '#fff', border: 'none', fontWeight: 600,
            }}
            onClick={onNextField}
          >
            Next: {nextFieldLabel || 'field'}
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
